import { Injectable } from '@angular/core';
import { ApiV6Service } from '@service/apiv6.service';
import { forkJoin, lastValueFrom } from 'rxjs';
import { getArrayField, getInternalAction, loadVariableName, prepareInfosDoc } from '@core/_utils/tools.utils';
import { MessagesService } from '@core/_services/messages.service';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { IndexedDbService } from './indexeddb.service';

@Injectable()
export class RequestTools {
    private currentDocId: string;
    private VariableName = {};

    constructor(private api: ApiV6Service, protected _messageService: MessagesService,
        private indexedDbService: IndexedDbService,
        private translate: TranslateService) {
        this.currentDocId = '';
        this.VariableName = loadVariableName(this.translate);
    }

    public setCurrentDocId(aCurrentDocId) {
        this.currentDocId = aCurrentDocId;
    }

    public async getVariableListTransfert(aBskType, aBskName) {
        let wResponse: any = {};
        return await lastValueFrom(this.api
            .getBskVariableList(aBskType, aBskName))
            .catch((error) => {
                console.log('getVariableListTransfert catch', error);
                return (wResponse = {
                    Status: 'ERROR',
                });
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }

    public async getVersionListSync() {
        return await lastValueFrom(this.api
            .getVersionList(this.currentDocId))
            .catch((error) => {
                console.log('getVersionListSync catch', error);
                return error;
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }

    public setFile(aAction) {
        return this.api.execGetUrlGedAction(aAction, '');
    }

    private getResponse(aResponse) {
        let wResponse: any = {};
        if (aResponse === undefined || aResponse === null) {
            return (wResponse = {
                status: false,
                Status: 'EXPIRED',
                Message: 'Expired',
            });
        }
        if (aResponse.type !== undefined) {
            if (aResponse.type === 'error') {
                return (wResponse = {
                    status: false,
                    Status: 'ERROR',
                    Message: aResponse.message !== undefined ? aResponse.message : '',
                });
            }
        }
        if (aResponse.Status !== undefined) {
            if (aResponse.Status === 'ERROR') {
                return (wResponse = {
                    status: false,
                    Status: aResponse.Status,
                    Message: aResponse.Message,
                });
            }
        }
        if (aResponse.status !== undefined) {
            if (aResponse.status !== 200) {
                return (wResponse = {
                    status: false,
                    Status: 'ERROR',
                    Message: aResponse.error !== null ? this.getErrorMessage(aResponse.error) : aResponse.statusText,
                });
            }
        }
        if (aResponse.exception !== undefined) {
            return (wResponse = {
                status: false,
                Status: 'ERROR',
                Message: aResponse.message !== undefined ? aResponse.message : '',
            });
        }
        return (wResponse = {
            status: true,
            Status: aResponse.Status !== undefined ? aResponse.Status : '',
            Message: '',
            data: aResponse,
        });
    }

    private getErrorMessage(aError) {
        let wMessage = 'TimeOut';
        if (Array.isArray(aError)) {
            if (aError[0].message !== undefined) {
                wMessage = aError[0].message;
            }
        }
        if (aError.message !== undefined) {
            wMessage = aError.message;
        }
        if (aError.Message !== undefined) {
            wMessage = aError.Message;
        }
        if (aError) {
            console.log('getErrorMessage catch', aError);
        }
        return wMessage;
    }

    public getAccessRight() {
        return forkJoin([
            this.api.getRootList(false, null, true),
            this.api.execUrlAction('getaccessright',''),
            this.api.execUrlAction('getgrouplist', ''),
            this.api.execUrlAction('getprofillist', '')
            ]).pipe(
            map(([getRootPath, getAccessRight, getGroupList, getProfilList]) => {
                return { getRootPath, getAccessRight, getGroupList, getProfilList };
            })
        );
    }

    public async getAsyncAccessRightUpdate() {
        return await lastValueFrom(this.getAccessRightUpdate())
            .catch((error) => {
                console.log('getAsyncAccessRightUpdate catch', error);
                return error;
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }

    private getAccessRightUpdate() {
        return forkJoin([
            this.api.getRootList(false, null, true),
            this.api.execUrlAction('getaccessright',''),
            ]).pipe(
            map(([getRootPath, getAccessRight]) => {
                return { getRootPath, getAccessRight };
            })
        );
    }

    public getInfosDoc(aVariableStorage, aUpdate) {
        return forkJoin([
            this.getInformationDoc(aUpdate),
            this.api.getCommDoc(this.currentDocId),
            this.api.getIndexDoc(this.currentDocId, aVariableStorage),
            this.api.getTraceDoc(this.currentDocId),
            this.api.execUrlAction('baskets', this.currentDocId),
            this.api.getAtfDoc(this.currentDocId),
            this.api.getVersionList(this.currentDocId),
            this.api.getCustomEditorlist(),            
        ]).pipe(
            map(([getInfoDoc, getCommentsDoc, getIndexDoc, getTracesDoc, getBasketsDoc, getAtfDoc, getVersionDoc, getEditorList]) => {
                return {
                    getInfoDoc,
                    getCommentsDoc,
                    getIndexDoc,
                    getTracesDoc,
                    getBasketsDoc,
                    getAtfDoc,
                    getVersionDoc,
                    getEditorList,                
                };
            })
        );
    }

    public async getAsyncInfosDoc(aVariableStorage, aUpdate) {
        return await lastValueFrom(this.getInfosDoc(aVariableStorage, aUpdate))
            .catch((error) => {
                console.log('getAsyncInfosDoc catch', error);
                return error;
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }

    public getBasketListCount(atypebsk) {
        return forkJoin([
            this.api.execUrlAction('getbskcount', ''), 
            this.api.execUrlAction('getuserbskpos', ''),
            this.api.execUrlAction('gettagbaselist', ''),
            this.api.execUrlAction('gettagbaskets', ''),
            this.getBasketList(''), 
            ]).pipe(
            map(([ getbskListCount, getUserBskPos, getTagBase, getTagBaskets, getBasketList]) => {
                if (getUserBskPos['Status'] === 'ERROR' || 
                    getTagBase['Status'] === 'ERROR' || 
                    getTagBaskets['Status'] === 'ERROR' ||
                    getBasketList['Status'] === 'ERROR') {
                    throw new Error(this.translate.instant('Load_Data_KO'));
                };
                return { getbskListCount, getUserBskPos, getTagBase, getTagBaskets, getBasketList };
            })
        );
    }

    public async getBasketList(atypebsk) {
        let getBasketList: any;
        getBasketList = await this.execGetBasketList();
        return getBasketList;
    }

    public async execGetBasketList(aPref = '') {
        let wBasketList: any;
        wBasketList = await this.indexedDbService.getBasketList();
        return wBasketList;
    }

    public getBskTransList(aBskList, aPref = '') {
        let wRes = {
            BasketMailList: aBskList['BasketMailList' + aPref],
            BasketCollectList: aBskList['BasketCollectList' + aPref],
            BasketPublishList: aBskList['BasketPublishList' + aPref],
            BasketActionList: aBskList['BasketActionList' + aPref],
            BasketInputList: aBskList['BasketInputList' + aPref],
            BasketOutputList:aBskList['BasketOutputList' + aPref],
            BasketSystemList: aBskList['BasketSystemList' + aPref]
        };
        return wRes;
    }

    public getBasketListOld(aData, atypebsk) {
        return forkJoin([
            this.api.execUrlAction('getbasketlistold', aData),
            this.getBasketList('')
            ]).pipe(
            map(([getBasketOld, getBasketList]) => {
                return { getBasketOld, getBasketList };
            })
        );
    }

    public async getBasketDocListVariable(aRequest, aBskType, aBskName) {
        return await lastValueFrom(this.getBasketDocListVar(aRequest, aBskType, aBskName))
            .catch((error) => {
                console.log('getBasketDocListVariable catch', error);
                return error;
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }

    public getBasketDocListVar(aRequest, aBskType, aBskName) {
        return forkJoin([
            this.api.execUrlAction('execrequestsearchbsk', aRequest),
            this.api.getBskVariableList(aBskType, aBskName),
            this.api.getBskListByName(aBskType, aBskName),
            this.api.execUrlAction('getdocksbasket',{name: aBskName}),
        ]).pipe(
            map(([getBskDocList, getBskVariableList, getBskListByName, getDocks]) => {
                return { getBskDocList, getBskVariableList, getBskListByName, getDocks };
            })
        );
    }

    public async getBasketDocList(aRequest) {
        return await lastValueFrom(this.api.execUrlAction('execrequestsearchbsk', aRequest))
            .catch((error) => {
                console.log('getBasketDocList catch', error);
                return error;
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }

    public async execBasket(aAction, aRequest) {
        let wRes: any;
        let wAction: string;
        wAction = getInternalAction(aAction);
        wRes = await this.execUrlAction(wAction, aRequest);
        return wRes;
    }

    public async execUpdate(aAction, aRequest) {
        let wRes: any;
        wRes = false;
        switch (aAction) {
            case 'deleteged':
            case 'comment':
            case 'checkout':
            case 'checkin':
            case 'changedoc':
            case 'sendbsk':
            case 'index': {
                wRes = await this.execUrlAction(aAction, aRequest);
                break;
            }
        }
        return wRes;
    }

    public async verifyResetPwd(identifiant) {
        // const formData = new FormData();
        // formData.append('login', identifiant);
        return await lastValueFrom(this.api
            .execUrlAction('checkreset', {}))
            .catch((error) => {
                console.log('verifyResetPwd catch', error);
                return error;
            })
            .then((res: any) => {
                return this.getResponse(res);
            });
    }

    public async execUrlAction(aAction, aRequest) {
        return await lastValueFrom(this.api
            .execUrlAction(aAction, aRequest))
            .catch((error) => {
                console.log( aAction + ' catch', error);
                return error;
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    } 
    
    public async execVerifyCheckOut(aLogin) {
        let wRes: any;
        wRes = false;
        const wParams = { DocIDFilter: this.currentDocId };
        wRes = await this.execUrlAction('verifycheckout', wParams);
        if (wRes.status === true) {
            return this.verifyCheckUser(wRes.data, aLogin);
        } else {
            this._messageService.addErrorMsg(this.translate.instant('State_Doc_KO'));
            return false;
        }
    }

    private verifyCheckUser(aData, aLogin) {
        let wRes = false;
        let wUser = '';
        if (aData) {
            if (aData.CkOutList.CkOut) {
                if (aData.CkOutList.CkOut.CkOutDocID === this.currentDocId) {
                    wUser = getArrayField(aData.CkOutList.CkOut.CkOutUser);
                    if (wUser === '' || wUser === aLogin) {
                        wRes = true;
                    } else {
                        this._messageService.addErrorNoStick(this.translate.instant('Lock_Usr_1', {user: wUser}));
                    }
                }
            }
        }
        return wRes;
    }

    private async setUserBskFilter(aData) {
        let wResponse: any = {};
        return await lastValueFrom(this.api
            .execUrlAction('updatebskfilter', aData))
            .catch((error) => {
                console.log('setUserBskFilter catch', error);
                return (wResponse = {
                    Status: 'ERROR',
                });
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }
    
    public async getRequestListBasket(aAction, aCurrentIdReqentete, aWebReqDetails, aParam) {
        let wResponse: any = {};
        let wRep: any = {};
        return await lastValueFrom(this.api
            .getRequestPrepare(aAction, aParam))
            .catch((error) => {
                console.log('getRequestListBasket catch', error);
                return (wResponse = {
                    Status: 'ERROR',
                });
            })
            .then((success: any) => {
                if (success.Status === 'ERROR') {
                    return this.getResponse(success);
                } else {
                    return {
                        selectedRequestId: aCurrentIdReqentete,
                        requestList: this.updateRequestParam(success.data, aCurrentIdReqentete, aWebReqDetails),
                    };
                }
            });
    }
       
    private updateRequestParam(success, aCurrentIdReqentete, aWebReqDetails) {
        let wValue : any = '';
        if (aCurrentIdReqentete !== -1) {
            return success.map((item) => {
                if (item.id === aCurrentIdReqentete) {
                    item.webReqDetails.map((elem) => {
                        wValue = this.getValueFromNoLig(elem.noligne, aWebReqDetails);
                        if (wValue !== null) {
                            elem.valdefault = wValue;
                            elem.value = wValue;
                            elem.DefaultValue = wValue;
                        }
                    });
                }
                return item;
            });
        } else {
            return success;
        }
    }

    private getValueFromNoLig(aNoligne, aWebReqDetails) {
        let wArray = <any>[];
        let wValue = null;
        wArray = aWebReqDetails.filter((item) => {
            return item.noligne === aNoligne;
        });
        if (wArray.length > 0) {
            wValue = wArray[0].valdefault;
        }
        return wValue;
    }

    public async execSetUserBskFilter(
        aEditorService,
        aCurrentFilterId,
        aBskName,
        aCurrentIdReqentete,
        aVariableList,
        aCurrentFilterAuto
    ) {
        let wRes: any;
        let wIdRequest = -1;
        let wData: any;
        wIdRequest = this.getIdRequest(aVariableList);
        wData = {
            id: aCurrentFilterId,
            bskname: aBskName,
            requestid: aCurrentIdReqentete,
            datarequest: this.formatVariableFilter(aEditorService, aVariableList),
            filterauto: aCurrentFilterAuto,
        };
        wRes = await this.setUserBskFilter(wData);
        return wRes;
    }

    // Récup de l'id de la requête sélectionnée
    public getIdRequest(aVariableList) {
        let wIdRequest = -1;
        if (aVariableList.length > 0) {
            wIdRequest = aVariableList[0].idReqEntete;
        }
        return wIdRequest;
    }

    private formatVariableFilter(aEditorService, aVariableList) {
        let wData = <any>[];
        aVariableList.forEach((item) => {
            wData.push({
                id: item.id,
                noligne: item.noligne,
                valdefault: aEditorService.getValueFromType(item, false),
            });
        });
        return JSON.stringify(wData);
    }

    public setResultFilter(aUserBskFilter) {
        let wCurrentFilterParam = [];
        let wCurrentFilterId = 0;
        let wCurrentIdReqentete = -1;
        let wCurrentFilterAuto: any;
        if (aUserBskFilter.data.length > 0) {
            wCurrentFilterId = aUserBskFilter.data[0].id;
            wCurrentIdReqentete = aUserBskFilter.data[0].id_reqentete;
            wCurrentFilterAuto = aUserBskFilter.data[0].filterauto;
            wCurrentFilterParam = JSON.parse(aUserBskFilter.data[0].datarequest);
        }
        if (wCurrentFilterAuto === 1 || wCurrentFilterAuto === true) {
            wCurrentFilterAuto = true;
        } else {
            wCurrentFilterAuto = false;
        }
        return {
            currentFilterId: wCurrentFilterId,
            currentIdReqentete: wCurrentIdReqentete,
            currentFilterAuto: wCurrentFilterAuto,
            currentFilterParam: wCurrentFilterParam,
        };
    }
    
    public async readGroup(aData) {
        return await lastValueFrom(this.api.execUrlAction('getuserlistfilter', aData))
        .catch((error) => {
            console.log('readGroup catch', error);
            return error;
        })
        .then((success: any) => {
            return this.getResponse(success);
        });
    }

    public async readDelegate() {
        return await lastValueFrom(this.readDelegateData())
        .catch((error) => {
            console.log('readDelegate catch', error);
            return error;
        })
        .then((success: any) => {
            return this.getResponse(success);
        });
    }
    private readDelegateData() {
        return forkJoin([
            this.api.execUrlAction('getdelegatelist', ''),
            this.api.execUrlAction('getuserlist', ''),
            this.api.execUrlAction('getdelegatetusers', '')]).pipe(
            map(([getResultList, getUserList, getDelegateListUser]) => {
                return { getResultList, getUserList, getDelegateListUser };
            })
        );
    }

    public async getResultsList() {
        return await lastValueFrom(this.api.execUrlAction('getresultsbyuser',''))
        .catch((error) => {
            console.log('getResultsList catch', error);
            return error;
        })
        .then((success: any) => {
            return this.getResponse(success);
        });
    }
    
    public async getDisplayList(aId) {
        return await lastValueFrom(this.api.execUrlAction('getresult', aId ))
        .catch((error) => {
            console.log('getResultsList catch', error);
            return error;
        })
        .then((success: any) => {
            return this.getResponse(success);
        });
    }

    public async getUserPrefer() {
        return await lastValueFrom(this.getAllUserPreference())
            .catch((error) => {
                console.log('getUserPrefer catch', error);
                return error;
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }

    public getAllUserPreference() {
        return forkJoin([
            this.api.execUrlAction('getuserpreference', ''),
            this.api.execUrlAction('getconfiginfos', ''),
        ]).pipe(
            map(([getUserPref, getInformations]) => {
                return { getUserPref, getInformations };
            })
        );
    }
    
    public async setUserPref(aData) {
        let wResponse: any = {};
        return await lastValueFrom(this.api
            .execUrlAction('updatepreference', aData))
            .catch((error) => {
                console.log('setUserPref catch', error);
                return (wResponse = {
                    Status: 'ERROR',
                });
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }

    public async setAllUserPref() {
        let wResponse: any = {};
        return await lastValueFrom(this.api
            .execUrlAction('updateallpreference', ''))
            .catch((error) => {
                console.log('setAllUserPref catch', error);
                return (wResponse = {
                    Status: 'ERROR',
                });
            })
            .then((success: any) => {
                return this.getResponse(success);
            });
    }
    
    public getUserInfos(aUser) {
        // const wDataRequest = { user: aUser };
        return this.api.execUrlAction('getuserinfos', {});
    }
    
    public getEditorInfos(aEditorName) {
        return this.api.getEditorInfos(aEditorName);
    }

    private getInformationDoc(aUpdate) {
        let wAction = 'infos';
        if (aUpdate === true) {
            wAction = 'infosupdate';
        }
        return this.api.execUrlAction(wAction, this.currentDocId).pipe(
            map((data: any) => {
                try {
                    let wdata: any;
                    wdata = data;
                    wdata = wdata.Document;
                    return {
                        Status: data.Status,
                        data: prepareInfosDoc(wdata, this.VariableName),
                    };
                } catch (error) {
                    console.log('getInformationDoc catch', error);
                    return error;
                }
            })
        );
    }

    public getBasketsDocWithId() {
        return this.api.getBasketsDocWithId(this.currentDocId);
    }    
}
